import React, { useEffect, lazy, Suspense } from 'react';
import { AppProvider, useAppContext } from './context/AppContext';
import Hero from './components/Hero.jsx';
import About from './components/About.jsx';
import Timeline from './components/Timeline.jsx';
import Services from './components/Services.jsx';
import Projects from './components/Projects.jsx';
import Expertise from './components/Expertise.jsx';
import Contact from './components/Contact.jsx';
import Navbar from './components/Navbar.jsx';
import Footer from './components/Footer.jsx';
import { scrollToSection } from './scrollUtils';

// Lazy load heavy components for code splitting
const PortfolioPage = lazy(() => import('./components/PortfolioPage'));
const ProjectDetail = lazy(() => import('./components/ProjectDetail'));
const AboutUsPage = lazy(() => import('./components/AboutUsPage'));

// Loading fallback component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-white text-lg font-semibold">Loading...</p>
    </div>
  </div>
);

const AppContent = () => {
  const { currentPage, setCurrentPage, selectedProject, setSelectedProject } = useAppContext();

  // Robust Hash-Based Routing for Mobile Back Button
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;

      if (hash === '#/portfolio') {
        setCurrentPage('portfolio');
      } else if (hash.startsWith('#/project/')) {
        const slug = hash.replace('#/project/', '');
        setSelectedProject(slug);
        setCurrentPage('project-detail');
      } else {
        // Handle home sections or root
        setCurrentPage('home');
      }
    };

    // Listen for hash changes (native browser behavior for back button)
    window.addEventListener('hashchange', handleHashChange);

    // Initial sync on mount
    handleHashChange();

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [setCurrentPage, setSelectedProject]);

  // Sync state changes back to the URL Hash
  useEffect(() => {
    const currentHash = window.location.hash;

    if (currentPage === 'portfolio' && currentHash !== '#/portfolio') {
      window.location.hash = '/portfolio';
    } else if (currentPage === 'project-detail' && selectedProject) {
      const targetHash = `#/project/${selectedProject}`;
      if (currentHash !== targetHash) {
        window.location.hash = `/project/${selectedProject}`;
      }
    } else if (currentPage === 'home' && currentHash.startsWith('#/')) {
      // If we are at home but hash suggests portfolio/project, clear it
      // unless it's a section anchor like #about
      window.location.hash = '';
    }
  }, [currentPage, selectedProject]);

  // Scroll to top/section when currentPage changes
  useEffect(() => {
    window.scrollTo(0, 0); // Always scroll to top on page change

    if (currentPage === 'home') {
      // Wait a bit for the content to render, then scroll to the section
      setTimeout(() => {
        const hash = window.location.hash.replace('#', '');
        if (hash && ['about', 'services', 'projects', 'contact', 'hero'].includes(hash)) {
          scrollToSection(hash);
        }
      }, 100);
    }
  }, [currentPage]);

  const renderPage = () => {
    switch(currentPage) {
      case 'portfolio':
        return <Suspense fallback={<LoadingSpinner />}><PortfolioPage setCurrentPage={setCurrentPage} /></Suspense>;
      case 'project-detail':
        return <Suspense fallback={<LoadingSpinner />}><ProjectDetail setCurrentPage={setCurrentPage} projectSlug={selectedProject} /></Suspense>;
      case 'about-us':
        return <Suspense fallback={<LoadingSpinner />}><AboutUsPage setCurrentPage={setCurrentPage} /></Suspense>;
      default:
        return (
          <>
            <Hero id="hero" />
            <About id="about" setCurrentPage={setCurrentPage} />
            <Services id="services" />
            <Projects setCurrentPage={setCurrentPage} id="projects" />
            <Expertise />
            <Contact id="contact" />
          </>
        );
    }
  };

  return (
    <div className="App overflow-x-hidden w-full max-w-[100vw]">
      <Navbar setCurrentPage={setCurrentPage} />
      <main>
        {renderPage()}
      </main>
      {currentPage === 'home' && <Footer />}
    </div>
  );
};

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;